import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Radio, Spin } from 'antd';
import { LOADER_POSITIONS, MULTI_ROLE_OPERATOR } from '../../../utils/staffPositions';
import styles from './EquipmentAllocationTable.module.css';

export const loaderSwapBlock = (vehicle) => {
  if (!vehicle || vehicle.archived) return 'Loader is unavailable';
  if ((vehicle.allocated_sites || []).some(site => !site.archived && site.staff_allocation_finalized)) {
    return 'Staff allocation is finalized at a loader site';
  }
  return '';
};

// "Client · Site" for every site the loader serves, e.g. "Amazon · 8550 Goreway, Walmart · North".
export const formatLoaderSites = (loader) => (loader?.allocated_sites || [])
  .map(site => [site.client_name, site.site_name].filter(Boolean).join(' · '))
  .join(', ');

export const buildSwapCandidates = (staff, vehicles, source) => {
  // This must be the full loader inventory, never the page's filteredVehicles.
  const byId = new Map(vehicles.map(vehicle => [String(vehicle.vehicle_id), vehicle]));
  return staff.filter(person => !person.archived && LOADER_POSITIONS.includes(person.position)
    && String(person.staff_id) !== String(source.staff_id)).map(person => {
    const assigned = person.assigned_loader;
    const loader = assigned ? byId.get(String(assigned.vehicle_id)) : null;
    const other = [person.assigned_walkway_crew_bool && 'Walkway crew', person.assigned_salting_route_bool && 'Salting route'].filter(Boolean);
    const sites = formatLoaderSites(loader);
    const allocation = assigned
      ? `${loader?.vehicle_name || assigned.vehicle_name} — ${sites || 'No sites allocated'}`
      : other.length ? `No loader allocation — ${other.join(', ')}` : 'Not allocated';
    const reason = person.position === MULTI_ROLE_OPERATOR && other.length
      ? `Already assigned to ${other.join(', ')}`
      : assigned ? loaderSwapBlock(loader) : '';
    return { ...person, allocation, reason, loader };
  });
};

export default function StaffSwapModal({ source, loadData, onSwap, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);
  const submitting = useRef(false);
  const mounted = useRef(true);
  const refresh = async (notice = '') => {
    const request = ++generation.current;
    setLoading(true);
    setSelectedId(null);
    setError(notice);
    try {
      const result = await loadData();
      if (generation.current === request) setData(result);
    } catch (err) {
      if (generation.current === request) {
        setData(null);
        setError('Could not load staff and loaders. Please retry.');
      }
    } finally {
      if (generation.current === request) setLoading(false);
    }
  };
  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => {
      mounted.current = false;
      generation.current += 1;
    };
    // The parent remounts this picker for each source/setup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const candidates = data ? buildSwapCandidates(data.staff, data.vehicles, source) : [];
  const selected = candidates.find(person => person.staff_id === selectedId);
  const currentLoader = data?.vehicles.find(vehicle => String(vehicle.vehicle_id) === String(source.vehicle_id));
  const currentStaff = currentLoader?.assigned_staff?.find(
    person => String(person.staff_id) === String(source.staff_id),
  );
  const sourceBlock = source.archived || currentStaff?.archived
    ? 'Archived staff cannot be swapped.'
    : data ? loaderSwapBlock(currentLoader) || (!currentStaff ? 'Current assignment changed. Close and reopen Site Details.' : '') : '';
  // Until fresh loader data arrives, fall back to what the opening popup knew.
  const currentLoaderName = currentLoader?.vehicle_name || source.vehicle_name || 'Unknown loader';
  const currentSites = data ? formatLoaderSites(currentLoader) || 'No sites allocated' : '';
  const visible = candidates.filter(person => `${person.name} ${person.allocation}`.toLowerCase().includes(search.toLowerCase()));
  const submit = async () => {
    if (!selected || selected.reason || sourceBlock || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    const request = generation.current;
    try {
      await onSwap(source, selected);
      if (request === generation.current) onClose();
    } catch (err) {
      if (request !== generation.current) return;
      const notice = err.response?.data?.message || err.response?.data?.error || err.message || 'Swap failed';
      if (err.response?.status === 409) await refresh(notice);
      else setError(notice);
    } finally {
      submitting.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <Modal
      open
      title={`Swap ${source.name}`}
      onCancel={busy ? undefined : onClose}
      closable={!busy}
      maskClosable={!busy}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            aria-label="Confirm swap"
            className={styles.siteModalSwapButton}
            disabled={loading || !selected || Boolean(selected.reason || sourceBlock)}
            loading={busy}
            onClick={submit}
          >
            Confirm swap
          </Button>
        </>
      )}
    >
      <p className={styles.staffSwapCurrent} aria-label="Current assignment">
        Currently on <strong>{currentLoaderName}</strong>
        {currentSites && <> — {currentSites}</>}
      </p>
      {error && <Alert type="error" showIcon message={error} />}
      {sourceBlock && <Alert type="warning" message={sourceBlock} />}
      {loading ? (
        <Spin aria-label="Loading swap candidates" />
      ) : !data ? (
        <Button onClick={() => refresh()}>Retry</Button>
      ) : (
        <>
          <Input
            aria-label="Search swap candidates"
            placeholder="Search staff, loader, or site"
            value={search}
            onChange={event => setSearch(event.target.value)}
            disabled={busy}
          />
          <div className={styles.staffSwapCandidates}>
            <Radio.Group
              value={selectedId}
              onChange={event => setSelectedId(event.target.value)}
              disabled={busy || Boolean(sourceBlock)}
              className={styles.staffSwapCandidateGroup}
            >
              {visible.map(person => (
                <div key={person.staff_id} className={styles.staffSwapCandidate}>
                  <Radio
                    value={person.staff_id}
                    disabled={Boolean(person.reason) || busy || Boolean(sourceBlock)}
                  >
                    <strong>{person.name}</strong> — {person.position}
                    <br />
                    {person.allocation}
                    {person.reason && <div>{person.reason}</div>}
                  </Radio>
                </div>
              ))}
            </Radio.Group>
            {!visible.length && <p>No matching staff.</p>}
          </div>
          {selected && (
            <div aria-label="Swap preview">
              <p>{selected.name} → {currentLoader?.vehicle_name}</p>
              <p>{source.name} → {selected.loader?.vehicle_name || 'No loader allocation'}</p>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
